import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import AppLayout from '@/components/layout/AppLayout';
import { trackPageView } from '@/lib/analytics';
import { captureAttribution } from '@/lib/attribution';   // r7.7 (077) first-touch UTM / referrer for the session
import HomePage from '@/pages/HomePage';
import { useTenant } from '@/context/TenantContext';   // r7.6 tenant front door
const TenantHomePage = lazy(() => import('@/pages/tenant/TenantHomePage'));   // r7.6
import VerticalLandingPage from '@/pages/VerticalLandingPage';
import MoodPage from '@/pages/MoodPage';
const MoodsIndexPage = lazy(() => import('@/pages/MoodsIndexPage'));   // r7.7 D1 /m — the 22 curated lists
const CuratedPage = lazy(() => import('@/pages/CuratedPage'));         // r7.7 D4 /curated/this-week (SNT-011)
const StreamPage = lazy(() => import('@/pages/StreamPage'));           // r7.7 D4 /stream (SNT-013)
import AskPage from '@/pages/AskPage';

// Lazy chunks
const EventDetailPage = lazy(() => import('@/pages/EventDetailPage'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const SignupPage = lazy(() => import('@/pages/SignupPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));   // r7.10 SIGN-005
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));     // r7.10 SIGN-005 ?token=…[&invite=1]
const MeLayout = lazy(() => import('@/pages/me/MeLayout'));
const MyBookingsPage = lazy(() => import('@/pages/me/MyBookingsPage'));
const MyWishlistPage = lazy(() => import('@/pages/me/MyWishlistPage'));
const MyPreferencesPage = lazy(() => import('@/pages/me/MyPreferencesPage'));
const MyProfilePage = lazy(() => import('@/pages/me/MyProfilePage'));
const CheckoutPage = lazy(() => import('@/pages/checkout/CheckoutPage'));   // r6
const TicketPage = lazy(() => import('@/pages/me/TicketPage'));             // r6
const MyTicketsPage = lazy(() => import('@/pages/tickets/MyTicketsPage'));            // r7.4
const TicketDetailPage = lazy(() => import('@/pages/tickets/TicketDetailPage'));      // r7.4
const MyTransfersPage = lazy(() => import('@/pages/transfers/MyTransfersPage'));      // r7.4
const ProtectionPage = lazy(() => import('@/pages/me/ProtectionPage'));               // r7.4
const ClaimPage = lazy(() => import('@/pages/transfers/ClaimPage'));                  // r7.4
const ListingPage = lazy(() => import('@/pages/transfers/ListingPage'));              // r7.4
const QueuePage = lazy(() => import('@/pages/QueuePage'));
const CommunityPage = lazy(() => import('@/pages/CommunityPage'));   // r7.2
const AlbumPage = lazy(() => import('@/pages/AlbumPage'));           // r7.2
const GroupRoomPage = lazy(() => import('@/pages/GroupRoomPage'));   // r7.2
const JoinRoomPage = lazy(() => import('@/pages/JoinRoomPage'));     // r7.2
const LoyaltyPage = lazy(() => import('@/pages/me/LoyaltyPage'));    // r7.3
const SntPlusPage = lazy(() => import('@/pages/me/SntPlusPage'));    // r7.3
const CompetitionPage = lazy(() => import('@/pages/CompetitionPage'));                 // r7.3
const CompetitionResultsPage = lazy(() => import('@/pages/CompetitionResultsPage'));   // r7.3
const MyCompetitionEntriesPage = lazy(() => import('@/pages/me/MyCompetitionEntriesPage')); // r7.3
const CompetitionConsolePage = lazy(() => import('@/pages/organizer/CompetitionConsolePage')); // r7.3
const OrganizerLayout = lazy(() => import('@/pages/organizer/OrganizerLayout'));
const OrganizerDashboard = lazy(() => import('@/pages/organizer/OrganizerDashboard'));
const EventWizardPage = lazy(() => import('@/pages/organizer/EventWizardPage')); // r7.0 DIY console (replaces r6 CreateEventPage)
const MyEventsPage = lazy(() => import('@/pages/organizer/MyEventsPage'));         // r7.0
const VenueRequestsPage = lazy(() => import('@/pages/organizer/VenueRequestsPage')); // r7.0
const ScannerPage = lazy(() => import('@/pages/organizer/ScannerPage'));           // r7.0
const InterestInboxPage = lazy(() => import('@/pages/organizer/InterestInboxPage')); // r7.1
const CertificatesPage = lazy(() => import('@/pages/organizer/CertificatesPage'));   // r7.1
const ScanStandalone = lazy(() => import('@/pages/organizer/ScanStandalone'));     // r7.0 staff
const EventReviewPage = lazy(() => import('@/pages/admin/EventReviewPage'));       // r7.0
const VenueRequestsAdminPage = lazy(() => import('@/pages/admin/VenueRequestsAdminPage')); // r7.0
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminIndex = lazy(() => import('@/pages/admin/AdminLayout').then((m) => ({ default: m.AdminIndex })));   // r7.5
const AdminVenuesPage = lazy(() => import('@/pages/admin/AdminVenuesPage'));
const AdminModerationPage = lazy(() => import('@/pages/admin/AdminModerationPage'));

// === Phase 9-10 + Movies + Attractions routes ===

// Static info pages
const PrivacyPage = lazy(() => import('@/pages/info/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/info/TermsPage'));
const DataSafetyPage = lazy(() => import('@/pages/info/DataSafetyPage'));   // r7.6 step 10 (Play data-safety page)
const RefundPolicyPage = lazy(() => import('@/pages/info/RefundPolicyPage'));
const HelpPage = lazy(() => import('@/pages/info/HelpPage'));
const AboutPage = lazy(() => import('@/pages/info/AboutPage'));
const ContactPage = lazy(() => import('@/pages/info/ContactPage'));
const SecurityPage = lazy(() => import('@/pages/info/SecurityPage'));

// Movies vertical
const MoviesLandingPage = lazy(() => import('@/pages/movies/MoviesLandingPage'));
const MovieDetailPage = lazy(() => import('@/pages/movies/MovieDetailPage'));

// Attractions / Workshops / Festivals
const AttractionsBookingPage = lazy(() => import('@/pages/attractions/AttractionsBookingPage'));
const WorkshopBookingPage = lazy(() => import('@/pages/attractions/WorkshopBookingPage'));
const FestivalBookingPage = lazy(() => import('@/pages/attractions/FestivalBookingPage'));

// DPDP customer pages
const DeleteAccountPage = lazy(() => import('@/pages/me/DeleteAccountPage'));
const DataExportPage = lazy(() => import('@/pages/me/DataExportPage'));

// Organizer pages
const BoostPage = lazy(() => import('@/pages/organizer/BoostPage'));
const CompTicketsPage = lazy(() => import('@/pages/organizer/CompTicketsPage'));
const SettlementsPage = lazy(() => import('@/pages/organizer/SettlementsPage'));
const AttractionConfigPage = lazy(() => import('@/pages/organizer/AttractionConfigPage'));

// Admin pages
const DlqAdminPage = lazy(() => import('@/pages/admin/DlqAdminPage'));
const SeatDesignerPage = lazy(() => import('@/pages/admin/SeatDesignerPage'));
const BoostApprovalPage = lazy(() => import('@/pages/admin/BoostApprovalPage'));
const MoviesAdminPage = lazy(() => import('@/pages/admin/MoviesAdminPage'));
const CinemasAdminPage = lazy(() => import('@/pages/admin/CinemasAdminPage'));
const AdminThemePage = lazy(() => import('@/pages/admin/AdminThemePage'));
const SupportWorkbenchPage = lazy(() => import('@/pages/admin/SupportWorkbenchPage'));     // r7.5
const SettlementsAdminPage = lazy(() => import('@/pages/admin/SettlementsAdminPage'));     // r7.5
const CommercialSettingsPage = lazy(() => import('@/pages/admin/CommercialSettingsPage')); // r7.5
const CampaignsAdminPage = lazy(() => import('@/pages/admin/CampaignsAdminPage'));         // r7.5
const FlagsAdminPage = lazy(() => import('@/pages/admin/FlagsAdminPage'));                 // r7.5
const UsersAdminPage = lazy(() => import('@/pages/admin/UsersAdminPage'));                 // r7.5
const ApiKeysAdminPage = lazy(() => import('@/pages/organizer/ApiKeysPage').then((m) => ({ default: m.ApiKeysAdminPage })));   // r7.5
const OrganizerCampaignsPage = lazy(() => import('@/pages/organizer/CampaignsPage'));      // r7.5
const OrganizerApiKeysPage = lazy(() => import('@/pages/organizer/ApiKeysPage'));          // r7.5
// r7.6 tenants — organizer console (tenant owner) + admin console + audience pool + consents
const TenantBrandingPage = lazy(() => import('@/pages/organizer/TenantBrandingPage'));
const TenantAudiencePage = lazy(() => import('@/pages/organizer/TenantAudiencePage'));
const TenantAppPage = lazy(() => import('@/pages/organizer/TenantAppPage'));
const AdminTenantsPage = lazy(() => import('@/pages/admin/AdminTenantsPage'));
const AdminAudiencePage = lazy(() => import('@/pages/admin/AdminAudiencePage'));
const MyConsentsPage = lazy(() => import('@/pages/me/MyConsentsPage'));          // r7.6 step 8
const UnsubscribePage = lazy(() => import('@/pages/UnsubscribePage'));           // r7.6 step 8 (row token + r7.5 campaign token) · r7.6.1 ?f= follow token
const ArtistPage = lazy(() => import('@/pages/ArtistPage'));                     // r7.6.1 /a/:slug
const MyFollowsPage = lazy(() => import('@/pages/me/MyFollowsPage'));            // r7.6.1 /me/follows
const ArtistsAdminPage = lazy(() => import('@/pages/admin/ArtistsAdminPage'));   // r7.6.1 verify / merge
const CategoryPage = lazy(() => import('@/pages/CategoryPage'));                 // r7.6.4 /c/:slug
const CategoriesPage = lazy(() => import('@/pages/CategoryPage').then((m) => ({ default: m.CategoriesPage })));   // r7.6.4 /c
const CategoriesAdminPage = lazy(() => import('@/pages/admin/CategoriesAdminPage'));   // r7.6.4 taxonomy.manage
const PricingAssistPage = lazy(() => import('@/pages/organizer/PricingAssistPage'));   // r7.7 A5 — Ask S proposes price rules (SNT-003)
const EventBookingsPage = lazy(() => import('@/pages/organizer/EventBookingsPage'));   // r7.7 B1 — bookings + complimentary change
const OrganizerSettingsPage = lazy(() => import('@/pages/organizer/OrganizerSettingsPage'));   // r7.7 C1 profile & brand (SNT-003)
const MarketingHubPage = lazy(() => import('@/pages/organizer/MarketingHubPage'));             // r7.7 C2 marketing hub
const OnboardingPage = lazy(() => import('@/pages/organizer/OnboardingPage'));                 // r7.7 D4 (SNT-012)
const AdminMarketingPage = lazy(() => import('@/pages/admin/AdminMarketingPage'));             // r7.7 C6 act for an organizer
const ToolsAdminPage = lazy(() => import('@/pages/admin/ToolsAdminPage'));                     // r7.7 C3
const BannersAdminPage = lazy(() => import('@/pages/admin/BannersAdminPage'));                 // r7.7 C4
const AnalyticsAdminPage = lazy(() => import('@/pages/admin/AnalyticsAdminPage'));             // r7.7 C5 (SNT-002)
const OutboxAdminPage = lazy(() => import('@/pages/admin/OutboxAdminPage'));                   // r7.7 G4

// Error pages (named exports)
const NotFoundPage = lazy(() => import('@/pages/error/ErrorPages').then(m => ({ default: m.NotFoundPage })));
const ServerErrorPage = lazy(() => import('@/pages/error/ErrorPages').then(m => ({ default: m.ServerErrorPage })));
const OfflinePage = lazy(() => import('@/pages/error/ErrorPages').then(m => ({ default: m.OfflinePage })));

const PageFallback = () => (
  <div className="container-editorial py-20 text-center text-ink-400">
    <div className="inline-block w-8 h-8 border-2 border-ink-700 border-t-brand-500 rounded-full animate-spin" />
  </div>
);

const Lazy = ({ children }) => <Suspense fallback={<PageFallback />}>{children}</Suspense>;

// r7.6 — `/` is the marketplace on the platform host and the tenant's front door on a tenant host / build.
function HomeSwitch() {
  const tenant = useTenant();
  if (tenant.isPlatform) return <HomePage />;
  return <Lazy><TenantHomePage /></Lazy>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <Lazy><ServerErrorPage /></Lazy>,
    children: [
      { index: true, element: <HomeSwitch /> },   // r7.6 marketplace home vs tenant front door

      // Verticals — r7.7 D4: /v/:slug is the canonical vertical page (SNT-001); /stream has its own page (SNT-013)
      { path: 'v/:slug', element: <VerticalLandingPage /> },
      { path: 'events', element: <Navigate to="/v/live-events" replace /> },   // r7.7: old bare vertical paths
      { path: 'plays', element: <Navigate to="/v/theatre" replace /> },
      { path: 'sports', element: <Navigate to="/v/sports" replace /> },
      { path: 'activities', element: <Navigate to="/v/attractions" replace /> },
      { path: 'stream', element: <Lazy><StreamPage /></Lazy> },
      { path: 'curated/this-week', element: <Lazy><CuratedPage /></Lazy> },
      { path: 'm', element: <Lazy><MoodsIndexPage /></Lazy> },
      { path: 'm/:slug', element: <MoodPage /> },
      { path: 'e/:slug', element: <Lazy><EventDetailPage /></Lazy> },
      { path: 'events/:slug', element: <Lazy><EventDetailPage /></Lazy> },
      { path: 'search', element: <Lazy><SearchPage /></Lazy> },
      { path: 'c', element: <Lazy><CategoriesPage /></Lazy> },              // r7.6.4 every category
      { path: 'c/:slug', element: <Lazy><CategoryPage /></Lazy> },          // r7.6.4 one category (+ ?sub=)
      { path: 'a/:slug', element: <Lazy><ArtistPage /></Lazy> },          // r7.6.1 artist page
      { path: 'artists/:slug', element: <Lazy><ArtistPage /></Lazy> },    // r7.6.1 alias
      { path: 'ask', element: <AskPage /> },
      { path: 'checkout/:bookingId', element: <Lazy><CheckoutPage /></Lazy> },
      { path: 'queue', element: <Lazy><QueuePage /></Lazy> },

      // r7.2 Communities
      { path: 'communities/:idOrSlug', element: <Lazy><CommunityPage /></Lazy> },
      { path: 'albums/:albumId', element: <Lazy><AlbumPage /></Lazy> },
      { path: 'rooms/join/:code?', element: <Lazy><JoinRoomPage /></Lazy> },
      { path: 'rooms/:roomId', element: <Lazy><GroupRoomPage /></Lazy> },
      { path: 'events/:eventId/competition', element: <Lazy><CompetitionPage /></Lazy> },          // r7.3
      { path: 'competitions/:id/results', element: <Lazy><CompetitionResultsPage /></Lazy> },      // r7.3

      // Movies vertical
      { path: 'movies', element: <Lazy><MoviesLandingPage /></Lazy> },
      { path: 'movies/:slug', element: <Lazy><MovieDetailPage /></Lazy> },

      // Attractions / Workshops / Festivals
      { path: 'attractions/:slug', element: <Lazy><AttractionsBookingPage /></Lazy> },
      { path: 'workshops/:slug', element: <Lazy><WorkshopBookingPage /></Lazy> },
      { path: 'festivals/:slug', element: <Lazy><FestivalBookingPage /></Lazy> },

      // Auth
      { path: 'login', element: <Lazy><LoginPage /></Lazy> },
      { path: 'unsubscribe', element: <Lazy><UnsubscribePage /></Lazy> },          // r7.6 ?t=<campaign token>
      { path: 'unsubscribe/:token', element: <Lazy><UnsubscribePage /></Lazy> },   // r7.6 consent-row token
      { path: 'signup', element: <Lazy><SignupPage /></Lazy> },
      { path: 'forgot', element: <Lazy><ForgotPasswordPage /></Lazy> },                 // r7.10 SIGN-005
      { path: 'reset-password', element: <Lazy><ResetPasswordPage /></Lazy> },           // r7.10 SIGN-005

      // Static info pages
      { path: 'privacy', element: <Lazy><PrivacyPage /></Lazy> },
      { path: 'terms', element: <Lazy><TermsPage /></Lazy> },
      { path: 'data-safety', element: <Lazy><DataSafetyPage /></Lazy> },   // r7.6 step 10
      { path: 'refund-policy', element: <Lazy><RefundPolicyPage /></Lazy> },
      { path: 'help', element: <Lazy><HelpPage /></Lazy> },
      { path: 'about', element: <Lazy><AboutPage /></Lazy> },
      { path: 'contact', element: <Lazy><ContactPage /></Lazy> },
      { path: 'security', element: <Lazy><SecurityPage /></Lazy> },

      // Error pages
      { path: 'offline', element: <Lazy><OfflinePage /></Lazy> },
      { path: '500', element: <Lazy><ServerErrorPage /></Lazy> },

      // /me/*
      {
        path: 'me',
        element: <Lazy><MeLayout /></Lazy>,
        children: [
          { index: true, element: <Lazy><MyProfilePage /></Lazy> },
          { path: 'bookings', element: <Lazy><MyBookingsPage /></Lazy> },
          { path: 'bookings/:bookingId', element: <Lazy><TicketPage /></Lazy> },
          { path: 'tickets', element: <Lazy><MyTicketsPage /></Lazy> },                 // r7.4
          { path: 'tickets/:ticketId', element: <Lazy><TicketDetailPage /></Lazy> },    // r7.4
          { path: 'transfers', element: <Lazy><MyTransfersPage /></Lazy> },             // r7.4
          { path: 'protection', element: <Lazy><ProtectionPage /></Lazy> },             // r7.4
          { path: 'protection/:policyId', element: <Lazy><ProtectionPage /></Lazy> },   // r7.4
          { path: 'wishlist', element: <Lazy><MyWishlistPage /></Lazy> },
          { path: 'preferences', element: <Lazy><MyPreferencesPage /></Lazy> },
          { path: 'consents', element: <Lazy><MyConsentsPage /></Lazy> },   // r7.6 scoped marketing consent
          { path: 'follows', element: <Lazy><MyFollowsPage /></Lazy> },     // r7.6.1 artists I follow
          { path: 'loyalty', element: <Lazy><LoyaltyPage /></Lazy> },      // r7.3
          { path: 'plus', element: <Lazy><SntPlusPage /></Lazy> },         // r7.3
          { path: 'competitions', element: <Lazy><MyCompetitionEntriesPage /></Lazy> }, // r7.3
          { path: 'data-export', element: <Lazy><DataExportPage /></Lazy> },
          { path: 'delete-account', element: <Lazy><DeleteAccountPage /></Lazy> },
        ],
      },

      // r7.4 — public claim link + resale listing
      { path: 'transfers/claim/:code', element: <Lazy><ClaimPage /></Lazy> },
      { path: 'resale/:transferId', element: <Lazy><ListingPage /></Lazy> },

      // /scan/:eventId — staff scanner, no organizer nav (r7.0)
      { path: 'scan/:eventId', element: <Lazy><ScanStandalone /></Lazy> },

      // /organizer/*
      {
        path: 'organizer',
        element: <Lazy><OrganizerLayout /></Lazy>,
        children: [
          { index: true, element: <Lazy><OrganizerDashboard /></Lazy> },
          { path: 'events', element: <Lazy><MyEventsPage /></Lazy> },
          { path: 'venues', element: <Lazy><VenueRequestsPage /></Lazy> },
          { path: 'events/new', element: <Lazy><EventWizardPage /></Lazy> },
          { path: 'events/:eventId/edit', element: <Lazy><EventWizardPage /></Lazy> },
          { path: 'events/:eventId/scan', element: <Lazy><ScannerPage /></Lazy> },
          { path: 'events/:eventId/bookings', element: <Lazy><EventBookingsPage /></Lazy> },   // r7.7 B1
          { path: 'events/:eventId/interests', element: <Lazy><InterestInboxPage /></Lazy> },   // r7.1
          { path: 'events/:eventId/certificates', element: <Lazy><CertificatesPage /></Lazy> }, // r7.1
          { path: 'settlements', element: <Lazy><SettlementsPage /></Lazy> },
          { path: 'pricing', element: <Lazy><PricingAssistPage /></Lazy> },            // r7.7 A5 price rules + Ask S
          { path: 'settings', element: <Lazy><OrganizerSettingsPage /></Lazy> },       // r7.7 C1
          { path: 'marketing', element: <Lazy><MarketingHubPage /></Lazy> },           // r7.7 C2
          { path: 'broadcast', element: <Navigate to="/organizer/marketing?tab=broadcast" replace /> },   // r7.7: old link
          { path: 'copy', element: <Navigate to="/organizer/marketing?tab=copy" replace /> },             // r7.7: old link
          { path: 'onboarding', element: <Lazy><OnboardingPage /></Lazy> },            // r7.7 D4 (SNT-012)
          { path: 'campaigns', element: <Lazy><OrganizerCampaignsPage /></Lazy> },   // r7.5 (flag campaign_manager)
          { path: 'api-keys', element: <Lazy><OrganizerApiKeysPage /></Lazy> },      // r7.5 partner API keys
          { path: 'branding', element: <Lazy><TenantBrandingPage /></Lazy> },        // r7.6 tenant owner: brand, palette, sender, subdomain
          { path: 'audience', element: <Lazy><TenantAudiencePage /></Lazy> },        // r7.6 tenant marketing consents + CSV export
          { path: 'app', element: <Lazy><TenantAppPage /></Lazy> },                  // r7.6 Android package, App Links, OTA manifest
          { path: 'events/:eventId/boost', element: <Lazy><BoostPage /></Lazy> },
          { path: 'events/:eventId/comps', element: <Lazy><CompTicketsPage /></Lazy> },
          { path: 'events/:eventId/attraction-config', element: <Lazy><AttractionConfigPage /></Lazy> },
          { path: 'events/:eventId/competition', element: <Lazy><CompetitionConsolePage /></Lazy> }, // r7.3
        ],
      },

      // /admin/*
      {
        path: 'admin',
        element: <Lazy><AdminLayout /></Lazy>,
        children: [
          { index: true, element: <Lazy><AdminIndex /></Lazy> },   // r7.5 first permitted section
          { path: 'venues', element: <Lazy><AdminVenuesPage /></Lazy> },
          { path: 'artists', element: <Lazy><ArtistsAdminPage /></Lazy> },   // r7.6.1 artists.manage
          { path: 'categories', element: <Lazy><CategoriesAdminPage /></Lazy> },   // r7.6.4 taxonomy.manage
          { path: 'events/review', element: <Lazy><EventReviewPage /></Lazy> },
          { path: 'venue-requests', element: <Lazy><VenueRequestsAdminPage /></Lazy> },
          { path: 'moderation', element: <Lazy><AdminModerationPage /></Lazy> },
          { path: 'dlq', element: <Lazy><DlqAdminPage /></Lazy> },
          { path: 'seat-designer', element: <Lazy><SeatDesignerPage /></Lazy> },
          { path: 'boost-approvals', element: <Lazy><BoostApprovalPage /></Lazy> },
          { path: 'movies', element: <Lazy><MoviesAdminPage /></Lazy> },
          { path: 'cinemas', element: <Lazy><CinemasAdminPage /></Lazy> },
          { path: 'theme', element: <Lazy><AdminThemePage /></Lazy> },
          { path: 'support', element: <Lazy><SupportWorkbenchPage /></Lazy> },        // r7.5 bookings.read_any
          { path: 'settlements', element: <Lazy><SettlementsAdminPage /></Lazy> },    // r7.5 settlements.read
          { path: 'commercial', element: <Lazy><CommercialSettingsPage /></Lazy> },   // r7.5 settings.commercial / protection.plans.write
          { path: 'campaigns', element: <Lazy><CampaignsAdminPage /></Lazy> },        // r7.5 campaigns.manage
          { path: 'flags', element: <Lazy><FlagsAdminPage /></Lazy> },                // r7.5 flags.read
          { path: 'users', element: <Lazy><UsersAdminPage /></Lazy> },                // r7.5 console.users.read · r7.7 new user / invite / chips
          { path: 'marketing', element: <Lazy><AdminMarketingPage /></Lazy> },        // r7.7 C6 marketing.assist
          { path: 'tools', element: <Lazy><ToolsAdminPage /></Lazy> },                // r7.7 C3 tools.manage
          { path: 'banners', element: <Lazy><BannersAdminPage /></Lazy> },            // r7.7 C4 content.moderate
          { path: 'analytics', element: <Lazy><AnalyticsAdminPage /></Lazy> },        // r7.7 C5 analytics.read
          { path: 'outbox', element: <Lazy><OutboxAdminPage /></Lazy> },              // r7.7 G4 notify.manage | qa.tools
          { path: 'api-keys', element: <Lazy><ApiKeysAdminPage /></Lazy> },           // r7.5 apikeys.manage_any
          { path: 'tenants', element: <Lazy><AdminTenantsPage /></Lazy> },            // r7.6 tenants.read / tenants.manage
          { path: 'tenants/:slug', element: <Lazy><AdminTenantsPage /></Lazy> },      // r7.6 one tenant (commercial fields, members, organizers)
          { path: 'data', element: <Lazy><AdminAudiencePage /></Lazy> },              // r7.6 audience pool (was a dead nav entry)
        ],
      },

      // 404 fallback
      { path: '*', element: <Lazy><NotFoundPage /></Lazy> },
    ],
  },
]);

// r6: SPA page views (PostHog $pageview + Meta PageView) on every navigation, consent-gated inside analytics.js
let _lastPath = null;
router.subscribe((state) => {
  const path = state.location.pathname + state.location.search;
  if (path !== _lastPath) { _lastPath = path; trackPageView(path); captureAttribution(state.location); }
});
